import PropTypes from 'prop-types';
import SimpleBarReact from 'simplebar-react';
import { alpha, styled } from '@mui/material/styles';
import { Box, SxProps, Theme } from '@mui/material';

const RootStyle = styled('div')({
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
  minWidth: 0,
});

const SimpleBarStyle = styled(SimpleBarReact)(({ theme }) => ({
  maxHeight: '100%',
  '& .simplebar-scrollbar': {
    '&:before': {
      backgroundColor: alpha(theme.palette.grey[600], 0.48),
    },
    '&.simplebar-visible:before': {
      opacity: 1,
    },
  },
  '& .simplebar-track.simplebar-vertical': {
    width: 10,
  },
  '& .simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
    height: 6,
  },
  '& .simplebar-mask': {
    zIndex: 'inherit',
  },
}));

function hasFixedHeight(sx?: SxProps<Theme>) {
  if (!sx || Array.isArray(sx) || typeof sx === 'function') {
    return false;
  }
  const style = sx as Record<string, unknown>;
  return (
    style.height === 1 ||
    style.height === '100%' ||
    style.height === '100vh' ||
    typeof style.maxHeight !== 'undefined'
  );
}

Scrollbar.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

/**
 * - Listing tables (no fixed height): native overflowX — avoids SimpleBar
 *   placeholder white-space bugs on stage.
 * - Sidebar / fixed-height areas (sx.height: 1): full SimpleBar vertical scroll.
 */
export default function Scrollbar({
  children,
  sx,
  ...other
}: {
  children: any;
  sx?: SxProps<Theme>;
}) {
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Tables / list pages — no SimpleBar
  if (isMobile || !hasFixedHeight(sx)) {
    return (
      <Box
        sx={{ overflowX: 'auto', width: '100%', ...sx }}
        {...other}
      >
        {children}
      </Box>
    );
  }

  // Sidebar (and other fixed-height panels) — SimpleBar with working vertical scroll
  return (
    <RootStyle>
      <SimpleBarStyle
        timeout={500}
        clickOnTrack={false}
        sx={sx}
        {...other}
      >
        {children}
      </SimpleBarStyle>
    </RootStyle>
  );
}
